/**
 * The online `SceneDriver`. There is no `BotDriver` here and not a single
 * engine transition: the server is authoritative, so every local action leaves
 * as an intent and every state change arrives as a `STATE_SYNC` the scene just
 * paints. `applyState` is the whole of the inbound half — it decides which of
 * the scene's three local surfaces (own-turn options, claim window, hand-end)
 * the new snapshot calls for, which is the job `BotDriver.advance` does solo.
 *
 * Two things it deliberately still computes locally, from the *rebuilt* state:
 * `canTsumo` and `ownTurnKongOptions` for our own turn, and `claimsFor` for
 * our own claim window. Both read only the local seat's own hand — the one
 * hand a filtered snapshot carries for real (`network/snapshotToState.ts`) —
 * so neither can be answered from placeholder tiles, and both are re-validated
 * server-side anyway before anything is applied.
 *
 * A window is identified by its `deadline`: the server stamps one per window,
 * so the client can tell "the window I already answered" from "a new window on
 * the next discard" without inventing an id. Without that, the broadcast that
 * follows another seat's answer would pop our claim bar back up after we had
 * answered, and the server would silently drop the second answer.
 *
 * **The table pauses on a drop** — the server's freeze (`SeatPresence.ts`), not
 * the local pause menu, which this driver has no `setPaused` for. The client
 * mirrors it: the overlay covers the table, local surfaces clear, no intent
 * leaves. Snapshots arriving meanwhile are still *painted* — only the decision
 * they offer is withheld — and the last is re-applied on resume, since a peer's
 * `STATE_SYNC` lands before the `OPPONENT_RECONNECTED` that clears the overlay.
 */

import { canTsumo, claimsFor, type MahjongState } from '../game/GameState.js';
import type { Claim, SeatClaims } from '../game/Claims.js';
import type { Seat } from '../game/types.js';
import type { KongDecision } from '../ai/types.js';
import type { MahjongSnapshot } from '../network/protocol.js';
import { NO_OVERLAY, isPausedOverlay, type OverlayView } from '../network/overlayState.js';
import { LOCAL_SEAT } from './Bootstrap.js';
import { ownTurnKongOptions } from './BotKong.js';
import {
  claimIntent,
  type MahjongTransport,
  type OnlineOverlaySurface,
  type OnlineStateListener,
} from './onlineTransport.js';
import type { SceneDriver, SceneDriverHooks } from './sceneDrivers.js';

// The transport's shape lives next door (200-line cap); it is re-exported here
// because the driver is what defines what a transport is *for*.
export type {
  MahjongOverlayTransport,
  MahjongTransport,
  OnlineOverlaySurface,
  OnlineStateListener,
} from './onlineTransport.js';

export interface OnlineDriverOptions {
  readonly hooks: SceneDriverHooks;
  /** Builds the transport, wiring its inbound state callback back into this driver. */
  readonly connect: (onState: OnlineStateListener) => MahjongTransport;
  /** Omitted in unit tests, which have no DOM to open a modal in. */
  readonly surface?: OnlineOverlaySurface;
}

export class OnlineDriver implements SceneDriver {
  private readonly hooks: SceneDriverHooks;
  private readonly surface: OnlineOverlaySurface | null;
  private readonly transport: MahjongTransport;
  /** Deadline of the window currently open, and of the last one we answered. */
  private openWindow: number | null = null;
  private answeredWindow: number | null = null;
  private overlay: OverlayView = NO_OVERLAY;
  /** The last authoritative state, replayed when the table un-pauses. */
  private latest: { state: MahjongState; snapshot: MahjongSnapshot } | null = null;

  constructor(opts: OnlineDriverOptions) {
    this.hooks = opts.hooks;
    this.surface = opts.surface ?? null;
    this.transport = opts.connect((state, snapshot) => this.applyState(state, snapshot));
  }

  /**
   * The seat the server sat us in. The fallback only covers the window before
   * `GAME_START` assigns one, during which there is no state to render anyway
   * — the first snapshot cannot arrive before the seat does.
   */
  get localSeat(): Seat {
    return this.transport.localSeat ?? LOCAL_SEAT;
  }

  start(): void {
    this.transport.attach();
    this.transport.overlay?.onOverlayChange((view) => this.applyOverlay(view));
  }

  /** True while the table is frozen: no intent may leave, and no local
   *  decision may be offered (see the file header). */
  private get paused(): boolean {
    return isPausedOverlay(this.overlay.overlay);
  }

  discard(tileId: number): void {
    if (this.paused) return;
    this.hooks.clearLocalUi();
    this.transport.sendDiscard(tileId);
  }

  declareTsumo(): void {
    if (this.paused) return;
    this.hooks.clearLocalUi();
    this.transport.sendTsumo();
  }

  declareKong(kong: KongDecision): void {
    if (this.paused) return;
    this.hooks.clearLocalUi();
    this.transport.sendKong(kong);
  }

  resolveClaim(choice: Claim | null): void {
    if (this.paused) return;
    this.recordAnswer();
    if (choice === null) this.transport.sendPass();
    else this.transport.sendClaim(claimIntent(choice));
  }

  resolveExposedKong(): void {
    if (this.paused) return;
    this.recordAnswer();
    this.transport.sendKong({ kind: 'exposed' });
  }

  nextHand(): void {
    if (this.paused) return;
    this.transport.sendNextHand();
  }

  dispose(): void {
    this.transport.detach();
    this.surface?.dispose();
    this.openWindow = null;
    this.answeredWindow = null;
    this.latest = null;
  }

  /** Inbound overlay state: paint it, and pause or resume the table with it. */
  private applyOverlay(view: OverlayView): void {
    const wasPaused = this.paused;
    this.overlay = view;
    this.surface?.update(view);
    if (this.paused) {
      this.hooks.clearLocalUi();
      return;
    }
    if (wasPaused && this.latest !== null) this.applyState(this.latest.state, this.latest.snapshot);
  }

  /** Inbound authoritative state: adopt it, then surface whatever local
   *  decision it hands us. Mirrors `BotDriver.advance`'s branch order. */
  private applyState(state: MahjongState, snapshot: MahjongSnapshot): void {
    this.latest = { state, snapshot };
    this.openWindow = snapshot.claimWindow?.deadline ?? null;
    this.hooks.setState(state);
    this.hooks.setMatchState(snapshot.matchState);
    // Painted, but nothing offered: a frozen table is still worth looking at.
    if (this.paused) {
      this.hooks.clearLocalUi();
      return;
    }
    if (state.phase === 'hand-end') {
      this.hooks.clearLocalUi();
      if (state.result !== null) this.hooks.onHandEnd(state.result, snapshot.handScore);
      return;
    }
    this.hooks.closeHandEnd();
    const claims = this.localClaims(state, snapshot);
    if (claims !== null) {
      this.hooks.onLocalClaimWindow(claims);
      return;
    }
    if (state.phase === 'awaiting-discard' && state.turn === this.localSeat) {
      this.hooks.onLocalTurn(canTsumo(state), ownTurnKongOptions(state, this.localSeat));
      return;
    }
    this.hooks.clearLocalUi();
  }

  /** Our own options against the open discard, or `null` when there is no
   *  window, we are not eligible, or we already answered this one. */
  private localClaims(state: MahjongState, snapshot: MahjongSnapshot): SeatClaims | null {
    const window = snapshot.claimWindow;
    if (window === null || window.deadline === this.answeredWindow) return null;
    if (!window.eligibleSeats.includes(this.localSeat)) return null;
    return claimsFor(state).find((c) => c.seat === this.localSeat) ?? null;
  }

  private recordAnswer(): void {
    this.answeredWindow = this.openWindow;
    this.hooks.clearLocalUi();
  }
}
